import * as React from 'react';
import { observer } from 'mobx-react';

import { urls } from 'shared/entities/domain';
import { LoadingStage } from 'shared/entities/meta';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import { LoaderSpinnerSize } from 'shared/newEntities/components/LoaderSpinner';
import {
  useProjectsStore,
  useProjectStore,
  useRouterStore,
  useUiStore
} from 'stores/index';
import {
  SelectorEntities,
  SelectorOptions
} from 'shared/newEntities/components/Selector';
import { usePageTemplateMenuStore } from 'stores/PageTemplateMenuStore';
import { ComponentType } from 'shared/entities/components/Component';

import BreadcrumbSelectorItem from '../BreadcrumbSelectorItem';
import Arrow from '../Arrow';

import '../Breadcrumbs.modules.scss';

type Props = {
  className?: string;
  withArrow: boolean;
};

const ProjectItem: React.FC<Props> = ({ withArrow, className }: Props) => {
  const routerStore = useRouterStore();
  const projectStore = useProjectStore();
  const projectsStore = useProjectsStore();
  const uiStore = useUiStore();
  const menuStore = usePageTemplateMenuStore();

  const [projectName, setProjectName] = React.useState<string>('');

  React.useEffect(() => {
    if (projectStore.entity) {
      setProjectName(projectStore.entity?.name);
    }
  }, [projectStore.entity?.name]);

  const options = React.useMemo((): SelectorOptions => {
    const entities = projectsStore.projects.items.reduce<SelectorEntities>(
      (acc, project) => ({
        ...acc,
        [project.id]: {
          id: project.id,
          title: project.name
        }
      }),
      {} as SelectorEntities
    );

    return {
      entities,
      keys: projectsStore.projects.keys
    };
  }, [projectsStore.projects.items, projectsStore.projects.keys]);

  const selectProject = React.useCallback(
    (projectId: string) => {
      if (uiStore.isMobileSize) {
        uiStore.menuState.close();
      }
      if (
        menuStore.activeSection &&
        menuStore.activeSection.action.type === ComponentType.link &&
        projectStore.projectId
      ) {
        const curPathName = menuStore.activeSection.action.to;
        const newPath = curPathName.replace(projectStore.projectId, projectId);
        routerStore.push(newPath);
        return;
      }

      // если на странице настроек кабинета или профиля, меняем проект
      if (routerStore.matchPath(urls.SETTINGS.mask)) {
        projectStore.reset();
        projectStore.load(projectId);
        return;
      }

      routerStore.push(
        urls.PROJECT.tabs.SCENARIOS.COMMON.create({
          projectId
        })
      );
    },
    [uiStore.isMobileSize, projectStore.projectId, menuStore.activeSection]
  );

  const disabled = !!routerStore.matchPath(urls.PROJECT.tabs.SETTINGS.mask);

  const projectId = projectStore.projectId;

  const loadingStage = projectsStore.projects.loadingStage;

  const shouldShowProjectLoader =
    projectStore.loadingStage.isLoading ||
    projectsStore.projects.loadingStage === LoadingStage.LOADING;

  if (!projectsStore.hasProjects && !shouldShowProjectLoader) {
    return null;
  }

  return (
    <>
      <Arrow styleName="breadcrumbs__item" />
      <BreadcrumbSelectorItem
        styleName="breadcrumbs__item"
        className={className}
        to={
          projectId
            ? urls.PROJECT.tabs.SCENARIOS.COMMON.create({
                projectId
              })
            : ''
        }
        mask={urls.PROJECT.tabs.SETTINGS.mask}
        value={projectStore.projectId}
        onOptionClick={selectProject}
        loadingStage={loadingStage}
        options={options}
        disabled={disabled}
        showSelector={!shouldShowProjectLoader}
      >
        {shouldShowProjectLoader ? (
          <LoaderSpinner size={LoaderSpinnerSize.sm} />
        ) : (
          projectName
        )}
      </BreadcrumbSelectorItem>
      {withArrow && <Arrow styleName="breadcrumbs__item" />}
    </>
  );
};

export default observer(ProjectItem);
